/**
 * /change-orders — Change order log across all projects.
 *
 * Select a project from the dropdown, then browse its PCO/OCO/CCO log
 * with submit and approve actions for pending COs.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useDeferredValue, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  createFilterOptions,
} from "@mui/material";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PreviewIcon from "@mui/icons-material/Preview";
import UndoIcon from "@mui/icons-material/Undo";
import { api, errorMessage } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { MoneyField } from "@/components/MoneyField";
import { formatMoney } from "@/lib/money";

export const Route = createFileRoute("/_auth/change-orders")({
  component: ChangeOrdersPage,
});

interface Project {
  id: string;
  code: string;
  name: string;
  status: string;
}

interface ChangeOrder {
  id: string;
  project_id: string;
  co_number: string;
  co_type: string;
  status: string;
  description: string | null;
  issued_date: string | null;
  submitted_date: string | null;
  approved_date: string | null;
  contract_amount: string;
  is_billable: boolean;
  schedule_impact_days: number | null;
  reversed_at: string | null;
  reversal_reason: string | null;
}

interface ImpactAmount {
  current: string;
  delta: string;
  projected: string;
}

interface ChangeOrderImpactPreview {
  change_order_id: string;
  status: string;
  already_applied: boolean;
  owner_contract: ImpactAmount;
  owner_sov: ImpactAmount;
  budget: ImpactAmount;
  commitment: ImpactAmount | null;
  forecast_eac: ImpactAmount;
  schedule: {
    current_completion_date: string | null;
    delta_days: number;
    projected_completion_date: string | null;
  };
  sov_lines: Array<{
    change_order_line_id: string;
    owner_sov_item_id: string | null;
    creates_new_item: boolean;
    item_code: string | null;
    description: string | null;
    amount_delta: string;
    quantity_delta: string | null;
    adjustment_kind: string;
    reason: string;
  }>;
}

interface ProjectListPage {
  items: Project[];
  total: number;
  limit: number;
  offset: number;
}

interface Phase {
  id: string;
  code: string;
  name: string;
  is_postable: boolean;
  is_active: boolean;
}

interface OwnerSovItem {
  id: string;
  item_code: string;
  description: string;
  billing_type: string;
  quantity: string | null;
  unit_of_measure: string | null;
  original_value: string;
}

interface OwnerContractWorkspace {
  contract_id: string;
  contract_no: string;
  title: string;
  contract_status: string;
  profile_status: string;
  currency_code: string;
  sov_items: OwnerSovItem[];
}

type SovTargetMode = "none" | "existing" | "new";

interface ChangeOrderLineDraft {
  clientId: string;
  phase_id: string;
  cost_type: string;
  description: string;
  budget_amount: string;
  sov_target_mode: SovTargetMode;
  owner_sov_item_id: string;
  sov_amount_delta: string;
  sov_quantity_delta: string;
  new_sov_item_code: string;
  new_sov_item_description: string;
  new_sov_billing_type: string;
  new_sov_unit_of_measure: string;
  sov_adjustment_kind: string;
  sov_adjustment_reason: string;
}

interface ChangeOrderForm {
  co_number: string;
  co_type: string;
  description: string;
  contract_amount: string;
  is_billable: boolean;
  schedule_impact_days: string;
  lines: ChangeOrderLineDraft[];
}

const COST_TYPES = ["labor", "material", "subcontract", "equipment", "other", "burden"];

const ownerSovFilter = createFilterOptions<OwnerSovItem>({
  limit: 100,
  stringify: (item) => `${item.item_code} ${item.description}`,
});

function newLineDraft(): ChangeOrderLineDraft {
  return {
    clientId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
    phase_id: "",
    cost_type: "subcontract",
    description: "",
    budget_amount: "0",
    sov_target_mode: "none",
    owner_sov_item_id: "",
    sov_amount_delta: "0",
    sov_quantity_delta: "",
    new_sov_item_code: "",
    new_sov_item_description: "",
    new_sov_billing_type: "progress",
    new_sov_unit_of_measure: "",
    sov_adjustment_kind: "scope_change",
    sov_adjustment_reason: "",
  };
}

function emptyForm(): ChangeOrderForm {
  return {
    co_number: "",
    co_type: "pco",
    description: "",
    contract_amount: "0",
    is_billable: true,
    schedule_impact_days: "",
    lines: [],
  };
}

function amountToCents(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value.replaceAll(",", ""));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

const projectsQueryOptions = (search: string) =>
  queryOptions({
    queryKey: ["projects-list", search],
    queryFn: () => {
      const query = new URLSearchParams({ limit: "50" });
      if (search.trim()) query.set("search", search.trim());
      return api<ProjectListPage>(`/api/projects?${query.toString()}`);
    },
    staleTime: 30_000,
  });

const changeOrdersQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: ["change-orders", projectId],
    queryFn: () => api<ChangeOrder[]>(`/api/projects/${projectId}/change-orders`),
    enabled: !!projectId,
  });

const changeOrderImpactQueryOptions = (coId: string) =>
  queryOptions({
    queryKey: ["change-order-impact", coId],
    queryFn: () => api<ChangeOrderImpactPreview>(`/api/projects/change-orders/${coId}/impact-preview`),
    enabled: !!coId,
  });

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  draft: "default",
  submitted: "warning",
  approved: "success",
  rejected: "error",
  void: "error",
};

const TYPE_COLOR: Record<string, "primary" | "secondary" | "warning"> = {
  pco: "primary",
  oco: "secondary",
  cco: "warning",
};

function ChangeOrdersPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;

  const qc = useQueryClient();
  const [projectSearch, setProjectSearch] = useState("");
  const deferredProjectSearch = useDeferredValue(projectSearch);
  const projectsQ = useQuery(projectsQueryOptions(deferredProjectSearch));
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const cosQ = useQuery(changeOrdersQueryOptions(selectedProjectId));

  const [addOpen, setAddOpen] = useState(false);
  const [impactCoId, setImpactCoId] = useState("");
  const impactQ = useQuery(changeOrderImpactQueryOptions(impactCoId));
  const [reverseCoId, setReverseCoId] = useState("");
  const [reversalReason, setReversalReason] = useState("");
  const [form, setForm] = useState<ChangeOrderForm>(emptyForm);
  const phasesQ = useQuery({
    queryKey: ["project-phases", selectedProjectId],
    queryFn: () => api<Phase[]>(`/api/projects/${selectedProjectId}/phases`),
    enabled: addOpen && !!selectedProjectId,
    staleTime: 60_000,
  });
  const ownerContractQ = useQuery({
    queryKey: ["owner-contract-workspace", selectedProjectId],
    queryFn: () =>
      api<OwnerContractWorkspace>(`/api/projects/${selectedProjectId}/owner-contract`),
    enabled: addOpen && form.co_type === "oco" && !!selectedProjectId,
    retry: false,
    staleTime: 30_000,
  });

  const postablePhases = useMemo(
    () => (phasesQ.data ?? []).filter((phase) => phase.is_active && phase.is_postable),
    [phasesQ.data],
  );
  const sovItems = ownerContractQ.data?.sov_items ?? [];
  const sovIntentLines = form.lines.filter((line) => line.sov_target_mode !== "none");
  const sovDeltaCents = sovIntentLines.reduce(
    (total, line) => total + (amountToCents(line.sov_amount_delta) ?? 0),
    0,
  );
  const contractCents = amountToCents(form.contract_amount);

  const formValidation = useMemo(() => {
    if (!form.co_number.trim()) return "Enter a change-order number.";
    if (contractCents === null) return "Enter a valid contract amount.";
    for (const [index, line] of form.lines.entries()) {
      const label = `Line ${index + 1}`;
      if (!line.phase_id) return `${label}: select a postable project phase.`;
      if (amountToCents(line.budget_amount) === null) {
        return `${label}: enter a valid budget impact.`;
      }
      if (line.sov_target_mode === "none") continue;
      if (form.co_type !== "oco" || !form.is_billable) {
        return `${label}: only billable owner change orders can modify the owner SOV.`;
      }
      if (!ownerContractQ.data) return `${label}: this project needs an owner contract first.`;
      const delta = amountToCents(line.sov_amount_delta);
      if (delta === null || delta === 0) return `${label}: enter a non-zero owner SOV impact.`;
      if (!line.sov_adjustment_reason.trim()) return `${label}: explain the SOV adjustment.`;
      if (line.sov_target_mode === "existing" && !line.owner_sov_item_id) {
        return `${label}: select the existing owner SOV item.`;
      }
      if (
        line.sov_target_mode === "new" &&
        (!line.new_sov_item_code.trim() || !line.new_sov_item_description.trim())
      ) {
        return `${label}: enter the new SOV item code and description.`;
      }
    }
    if (sovIntentLines.length > 0 && contractCents !== sovDeltaCents) {
      return "Owner SOV impacts must equal the change-order contract amount.";
    }
    return null;
  }, [contractCents, form, ownerContractQ.data, sovDeltaCents, sovIntentLines.length]);

  const addMut = useMutation({
    mutationFn: (p: ChangeOrderForm) =>
      api<ChangeOrder>(`/api/projects/${selectedProjectId}/change-orders`, {
        method: "POST",
        body: JSON.stringify({
          co_number: p.co_number,
          co_type: p.co_type,
          description: p.description || null,
          contract_amount: (amountToCents(p.contract_amount) ?? 0) / 100,
          is_billable: p.is_billable,
          schedule_impact_days: p.schedule_impact_days ? parseInt(p.schedule_impact_days) : null,
          lines: p.lines.map((line, index) => ({
            line_no: index + 1,
            phase_id: line.phase_id,
            cost_type: line.cost_type,
            description: line.description || null,
            budget_amount: (amountToCents(line.budget_amount) ?? 0) / 100,
            ...(line.sov_target_mode === "existing"
              ? {
                  owner_sov_item_id: line.owner_sov_item_id,
                  owner_contract_id: ownerContractQ.data?.contract_id,
                  sov_amount_delta: (amountToCents(line.sov_amount_delta) ?? 0) / 100,
                  sov_quantity_delta: line.sov_quantity_delta
                    ? parseFloat(line.sov_quantity_delta)
                    : null,
                  sov_adjustment_kind: line.sov_adjustment_kind,
                  sov_adjustment_reason: line.sov_adjustment_reason.trim(),
                }
              : {}),
            ...(line.sov_target_mode === "new"
              ? {
                  owner_contract_id: ownerContractQ.data?.contract_id,
                  creates_new_sov_item: true,
                  sov_amount_delta: (amountToCents(line.sov_amount_delta) ?? 0) / 100,
                  sov_quantity_delta: line.sov_quantity_delta
                    ? parseFloat(line.sov_quantity_delta)
                    : null,
                  new_sov_item_code: line.new_sov_item_code.trim(),
                  new_sov_item_description: line.new_sov_item_description.trim(),
                  new_sov_billing_type: line.new_sov_billing_type,
                  new_sov_unit_of_measure: line.new_sov_unit_of_measure.trim() || null,
                  sov_adjustment_kind: line.sov_adjustment_kind,
                  sov_adjustment_reason: line.sov_adjustment_reason.trim(),
                }
              : {}),
          })),
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["change-orders", selectedProjectId] });
      setAddOpen(false);
      setForm(emptyForm());
    },
  });

  const submitMut = useMutation({
    mutationFn: (coId: string) =>
      api<ChangeOrder>(`/api/projects/change-orders/${coId}/submit`, { method: "POST" }),
    onSuccess: (_data, coId) => {
      void qc.invalidateQueries({ queryKey: ["change-orders", selectedProjectId] });
      void qc.invalidateQueries({ queryKey: ["change-order-impact", coId] });
    },
  });

  const approveMut = useMutation({
    mutationFn: (coId: string) =>
      api<ChangeOrder>(`/api/projects/change-orders/${coId}/approve`, { method: "POST" }),
    onSuccess: (_data, coId) => {
      void qc.invalidateQueries({ queryKey: ["change-orders", selectedProjectId] });
      void qc.invalidateQueries({ queryKey: ["change-order-impact", coId] });
    },
  });

  const reverseMut = useMutation({
    mutationFn: ({ coId, reason }: { coId: string; reason: string }) =>
      api<ChangeOrder>(`/api/projects/change-orders/${coId}/reverse`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["change-orders", selectedProjectId] });
      void qc.invalidateQueries({ queryKey: ["change-order-impact"] });
      setReverseCoId("");
      setReversalReason("");
    },
  });

  const projects = projectsQ.data?.items ?? [];
  const changeOrders = cosQ.data ?? [];
  const updateLine = (clientId: string, patch: Partial<ChangeOrderLineDraft>) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) =>
        line.clientId === clientId ? { ...line, ...patch } : line,
      ),
    }));
  };
  const closeAddDialog = () => {
    setAddOpen(false);
    setForm(emptyForm());
    addMut.reset();
  };
  const selectCoType = (coType: string) => {
    setForm((current) => ({
      ...current,
      co_type: coType,
      lines:
        coType === "oco"
          ? current.lines
          : current.lines.map((line) => ({
              ...line,
              sov_target_mode: "none" as const,
              owner_sov_item_id: "",
            })),
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChangeCircleIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Change Orders</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            PCO · OCO · CCO log per project
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!selectedProjectId}
          onClick={() => {
            setForm(emptyForm());
            setAddOpen(true);
          }}
        >
          New CO
        </Button>
      </Box>

      {/* Server-filtered project picker: do not load thousands of projects into the DOM. */}
      <Autocomplete
        size="small"
        sx={{ mb: 3, width: { xs: "100%", sm: 420 } }}
        options={projects}
        value={selectedProject}
        loading={projectsQ.isFetching}
        filterOptions={(options) => options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(project) => `${project.code} — ${project.name}`}
        onInputChange={(_event, value, reason) => {
          if (reason === "input" || reason === "clear") setProjectSearch(value);
        }}
        onChange={(_event, project) => {
          setSelectedProject(project);
          setSelectedProjectId(project?.id ?? "");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Project"
            placeholder="Search by project number or name"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {projectsQ.isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={projectSearch.trim() ? "No matching projects" : "No projects available"}
      />

      {!selectedProjectId ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
          Select a project above to view its change orders.
        </Paper>
      ) : cosQ.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>CO #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Schedule Impact</TableCell>
                <TableCell align="right">Contract Amount</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changeOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No change orders for this project. Click New CO to create one.
                  </TableCell>
                </TableRow>
              )}
              {changeOrders.map((co) => (
                <TableRow key={co.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{co.co_number}</TableCell>
                  <TableCell>
                    <Chip
                      label={co.co_type.toUpperCase()}
                      size="small"
                      color={TYPE_COLOR[co.co_type] ?? "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={co.status === "void" ? "voided" : co.status}
                      size="small"
                      color={STATUS_COLOR[co.status] ?? "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", maxWidth: 240 }}>
                    {co.description ?? "—"}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {co.schedule_impact_days != null ? `${co.schedule_impact_days}d` : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                    {formatMoney(co.contract_amount)}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                    {[
                      co.issued_date && `Issued: ${co.issued_date}`,
                      co.submitted_date && `Sub: ${co.submitted_date}`,
                      co.approved_date && `Appr: ${co.approved_date}`,
                    ].filter(Boolean).join(" · ") || "—"}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        startIcon={<PreviewIcon />}
                        onClick={() => setImpactCoId(co.id)}
                      >
                        Impact
                      </Button>
                      {co.status === "draft" && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={submitMut.isPending}
                          onClick={() => submitMut.mutate(co.id)}
                        >
                          Submit
                        </Button>
                      )}
                      {co.status === "submitted" && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={approveMut.isPending}
                          onClick={() => approveMut.mutate(co.id)}
                        >
                          Approve
                        </Button>
                      )}
                      {co.status === "approved" && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<UndoIcon />}
                          onClick={() => setReverseCoId(co.id)}
                        >
                          Reverse
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add CO dialog */}
      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="lg" fullWidth>
        <DialogTitle>New Change Order</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="CO Number"
              size="small"
              required
              value={form.co_number}
              onChange={(event) =>
                setForm((current) => ({ ...current, co_number: event.target.value }))
              }
            />
            <FormControl size="small">
              <InputLabel id="change-order-type-label">Type</InputLabel>
              <Select
                id="change-order-type"
                labelId="change-order-type-label"
                value={form.co_type}
                label="Type"
                onChange={(event) => selectCoType(event.target.value)}
              >
                <MenuItem value="pco">PCO (Potential)</MenuItem>
                <MenuItem value="oco">OCO (Owner)</MenuItem>
                <MenuItem value="cco">CCO (Construction)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(event) =>
              setForm((current) => ({ ...current, description: event.target.value }))
            }
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <MoneyField
              label="Contract Amount"
              size="small"
              value={form.contract_amount}
              onChange={(value) =>
                setForm((current) => ({ ...current, contract_amount: value }))
              }
            />
            <TextField
              label="Schedule Impact (days)"
              type="number"
              size="small"
              value={form.schedule_impact_days}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  schedule_impact_days: event.target.value,
                }))
              }
            />
          </Box>

          {form.co_type === "oco" && ownerContractQ.isLoading && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Loading the owner contract and SOV…
            </Alert>
          )}
          {form.co_type === "oco" && ownerContractQ.error && (
            <Alert severity="warning">
              No owner-contract workspace is available for this project. You can still create
              the change order, but SOV targeting requires an activated owner contract.
            </Alert>
          )}
          {form.co_type === "oco" && ownerContractQ.data && (
            <Alert severity="success">
              Owner contract {ownerContractQ.data.contract_no} · {sovItems.length.toLocaleString()} SOV
              {sovItems.length === 1 ? " item" : " items"}. Search results are capped at 100 rows
              so large schedules remain responsive.
            </Alert>
          )}

          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Scope and budget lines</Typography>
              <Typography variant="body2" color="text.secondary">
                Each line updates the selected phase budget. Billable owner CO lines can also
                target an existing SOV item or create a new one.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                setForm((current) => ({ ...current, lines: [...current.lines, newLineDraft()] }))
              }
            >
              Add line
            </Button>
          </Box>

          {form.lines.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              No scope lines yet. Header-only change orders are supported, or add a line to update
              budget and owner SOV views through one approval.
            </Paper>
          )}

          {form.lines.map((line, index) => {
            const selectedSov =
              sovItems.find((item) => item.id === line.owner_sov_item_id) ?? null;
            return (
              <Paper key={line.clientId} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Line {index + 1}</Typography>
                  <Tooltip title="Remove line">
                    <IconButton
                      size="small"
                      aria-label={`Remove line ${index + 1}`}
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          lines: current.lines.filter((candidate) => candidate.clientId !== line.clientId),
                        }))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    select
                    required
                    size="small"
                    label="Project phase"
                    value={line.phase_id}
                    onChange={(event) => updateLine(line.clientId, { phase_id: event.target.value })}
                    helperText={
                      phasesQ.isLoading
                        ? "Loading phases…"
                        : postablePhases.length === 0
                          ? "No active postable phases found"
                          : undefined
                    }
                  >
                    {postablePhases.map((phase) => (
                      <MenuItem key={phase.id} value={phase.id}>
                        {phase.code} — {phase.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Cost type"
                    value={line.cost_type}
                    onChange={(event) => updateLine(line.clientId, { cost_type: event.target.value })}
                  >
                    {COST_TYPES.map((costType) => (
                      <MenuItem key={costType} value={costType}>
                        {costType.replaceAll("_", " ")}
                      </MenuItem>
                    ))}
                  </TextField>
                  <MoneyField
                    label="Budget impact"
                    size="small"
                    value={line.budget_amount}
                    onChange={(value) => updateLine(line.clientId, { budget_amount: value })}
                  />
                </Box>
                <TextField
                  label="Scope description"
                  size="small"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={line.description}
                  onChange={(event) => updateLine(line.clientId, { description: event.target.value })}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="SOV target"
                    value={line.sov_target_mode}
                    onChange={(event) =>
                      updateLine(line.clientId, {
                        sov_target_mode: event.target.value as SovTargetMode,
                        owner_sov_item_id: "",
                      })
                    }
                  >
                    <MenuItem value="none">No SOV impact</MenuItem>
                    {form.co_type === "oco" && ownerContractQ.data && (
                      <MenuItem value="existing">Adjust existing SOV item</MenuItem>
                    )}
                    {form.co_type === "oco" && ownerContractQ.data && (
                      <MenuItem value="new">Create new SOV item</MenuItem>
                    )}
                  </TextField>

                  {line.sov_target_mode === "existing" && (
                    <Autocomplete
                      size="small"
                      options={sovItems}
                      value={selectedSov}
                      filterOptions={ownerSovFilter}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(item) => `${item.item_code} — ${item.description}`}
                      onChange={(_event, item) =>
                        updateLine(line.clientId, { owner_sov_item_id: item?.id ?? "" })
                      }
                      renderOption={(props, item) => (
                        <Box component="li" {...props} key={item.id}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {item.item_code} — {item.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Original {formatMoney(item.original_value)} · {item.billing_type.replaceAll("_", " ")}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} required label="Existing SOV item" />
                      )}
                      noOptionsText="No matching SOV item"
                    />
                  )}
                </Box>

                {line.sov_target_mode === "new" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 2fr 1fr 1fr" },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <TextField
                      required
                      size="small"
                      label="New SOV code"
                      value={line.new_sov_item_code}
                      onChange={(event) =>
                        updateLine(line.clientId, { new_sov_item_code: event.target.value })
                      }
                    />
                    <TextField
                      required
                      size="small"
                      label="New SOV description"
                      value={line.new_sov_item_description}
                      onChange={(event) =>
                        updateLine(line.clientId, { new_sov_item_description: event.target.value })
                      }
                    />
                    <TextField
                      select
                      size="small"
                      label="Billing type"
                      value={line.new_sov_billing_type}
                      onChange={(event) =>
                        updateLine(line.clientId, { new_sov_billing_type: event.target.value })
                      }
                    >
                      <MenuItem value="progress">Progress</MenuItem>
                      <MenuItem value="unit_price">Unit price</MenuItem>
                      <MenuItem value="tm">Time & materials</MenuItem>
                      <MenuItem value="both">Progress + unit</MenuItem>
                      <MenuItem value="not_billable">Not billable</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label="Unit of measure"
                      value={line.new_sov_unit_of_measure}
                      onChange={(event) =>
                        updateLine(line.clientId, { new_sov_unit_of_measure: event.target.value })
                      }
                    />
                  </Box>
                )}

                {line.sov_target_mode !== "none" && (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <MoneyField
                        label="Owner SOV amount impact"
                        size="small"
                        value={line.sov_amount_delta}
                        onChange={(value) =>
                          updateLine(line.clientId, { sov_amount_delta: value })
                        }
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Quantity impact"
                        value={line.sov_quantity_delta}
                        onChange={(event) =>
                          updateLine(line.clientId, { sov_quantity_delta: event.target.value })
                        }
                      />
                      <TextField
                        select
                        size="small"
                        label="Adjustment kind"
                        value={line.sov_adjustment_kind}
                        onChange={(event) =>
                          updateLine(line.clientId, { sov_adjustment_kind: event.target.value })
                        }
                      >
                        <MenuItem value="scope_change">Scope change</MenuItem>
                        <MenuItem value="baseline_correction">Baseline correction</MenuItem>
                      </TextField>
                    </Box>
                    <TextField
                      required
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      label="SOV adjustment reason"
                      sx={{ mt: 2 }}
                      value={line.sov_adjustment_reason}
                      onChange={(event) =>
                        updateLine(line.clientId, { sov_adjustment_reason: event.target.value })
                      }
                      helperText="Stored with the immutable approval and reversal audit chain."
                    />
                  </>
                )}
              </Paper>
            );
          })}

          {sovIntentLines.length > 0 && (
            <Alert severity={contractCents === sovDeltaCents ? "success" : "error"}>
              Owner SOV impacts total {formatMoney((sovDeltaCents / 100).toFixed(2))}; change-order
              contract amount is {formatMoney(((contractCents ?? 0) / 100).toFixed(2))}. These must
              match before creation.
            </Alert>
          )}
          {formValidation && (
            <Typography variant="body2" color="text.secondary">
              To create: {formValidation}
            </Typography>
          )}
          {addMut.error && <Alert severity="error">{errorMessage(addMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={addMut.isPending || !!formValidation}
            onClick={() => addMut.mutate(form)}
          >
            {addMut.isPending ? "Creating…" : "Create change order"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!impactCoId}
        onClose={() => setImpactCoId("")}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Combined change-order impact</DialogTitle>
        <DialogContent>
          {impactQ.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : impactQ.error ? (
            <Alert severity="error">{errorMessage(impactQ.error)}</Alert>
          ) : impactQ.data ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {impactQ.data.already_applied && (
                <Alert severity="info">
                  This change is already approved. The current values include its impact.
                </Alert>
              )}
              {impactQ.data.status === "void" && (
                <Alert severity="warning">
                  This change order was reversed. Current values exclude its impact; the SOV
                  lines below preserve the original approved intent for audit.
                </Alert>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {([
                  ["Owner contract", impactQ.data.owner_contract],
                  ["Owner SOV", impactQ.data.owner_sov],
                  ["Cost budget", impactQ.data.budget],
                  ["Forecast EAC", impactQ.data.forecast_eac],
                  ...(impactQ.data.commitment ? [["Commitment", impactQ.data.commitment] as const] : []),
                ] as Array<readonly [string, ImpactAmount]>).map(([label, amount]) => (
                  <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="overline" color="text.secondary">{label}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 0.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Current</Typography>
                        <Typography fontFamily="monospace">{formatMoney(amount.current)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Change</Typography>
                        <Typography fontFamily="monospace" color={Number(amount.delta) < 0 ? "error.main" : "primary.main"}>
                          {formatMoney(amount.delta)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Projected</Typography>
                        <Typography fontFamily="monospace" fontWeight={600}>{formatMoney(amount.projected)}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="overline" color="text.secondary">Schedule</Typography>
                <Typography>
                  {impactQ.data.schedule.current_completion_date ?? "No completion date"}
                  {impactQ.data.schedule.delta_days !== 0 &&
                    impactQ.data.schedule.projected_completion_date &&
                    ` → ${impactQ.data.schedule.projected_completion_date}`}
                  {` (${impactQ.data.schedule.delta_days >= 0 ? "+" : ""}${impactQ.data.schedule.delta_days} days)`}
                </Typography>
              </Paper>
              {impactQ.data.sov_lines.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600}>Owner SOV lines</Typography>
                  {impactQ.data.sov_lines.map((line) => (
                    <Paper key={line.change_order_line_id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Box>
                          <Typography fontWeight={600}>
                            {line.item_code ?? "New line"} — {line.description ?? "Untitled scope"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {line.reason} · {line.adjustment_kind.replaceAll("_", " ")}
                          </Typography>
                        </Box>
                        <Typography fontFamily="monospace" fontWeight={600}>
                          {formatMoney(line.amount_delta)}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpactCoId("")}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!reverseCoId} onClose={() => setReverseCoId("")} maxWidth="sm" fullWidth>
        <DialogTitle>Reverse approved change order</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This creates compensating budget, contract, schedule, and SOV entries. It does not delete history.
          </Alert>
          <TextField
            label="Reversal reason"
            value={reversalReason}
            onChange={(event) => setReversalReason(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            required
          />
          {reverseMut.error && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(reverseMut.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseCoId("")}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reversalReason.trim() || reverseMut.isPending}
            onClick={() => reverseMut.mutate({ coId: reverseCoId, reason: reversalReason })}
          >
            {reverseMut.isPending ? "Reversing…" : "Reverse change order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
